import React, { useMemo, useState, useEffect } from 'react';
import { Box, Typography, Paper, CircularProgress, Alert, Grid, Avatar, Pagination, useTheme, useMediaQuery } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Bar } from 'react-chartjs-2';
import { Chart, CategoryScale, LinearScale, BarElement, Title, Tooltip as ChartTooltip, Legend } from 'chart.js';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { useGuidanceStudents } from '../contexts/GuidanceStudentsContext';
import { useSchools } from '../contexts/SchoolsContext';
import { usePrograms } from '../contexts/ProgramsContext';
import { useEvents } from '../contexts/EventsContext';
import { parseDateSafariCompatible } from '../utils/dateDebugUtils';

// Register Chart.js components
Chart.register(CategoryScale, LinearScale, BarElement, Title, ChartTooltip, Legend);

const GuidanceDashboard: React.FC = () => {
  const { user } = useUser();
  const { students, loading, error } = useGuidanceStudents();
  const { schools } = useSchools();
  const { programs } = usePrograms();
  const { events: allEvents, loading: eventsLoading } = useEvents();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [eventsPage, setEventsPage] = useState(1);
  const eventsPerPage = 5;

  // Determine the next 4 graduating classes based on current month
  const graduatingClasses = useMemo(() => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1; // getMonth() returns 0-11, so add 1 for 1-12

    if (currentMonth < 7) {
      // Jan-Jun: current year, current year +1, current year +2, current year +3
      return [
        currentYear.toString(),
        (currentYear + 1).toString(),
        (currentYear + 2).toString(),
        (currentYear + 3).toString(),
      ];
    } else {
      // Jul-Dec: current year +1, current year +2, current year +3, current year +4
      return [
        (currentYear + 1).toString(),
        (currentYear + 2).toString(),
        (currentYear + 3).toString(),
        (currentYear + 4).toString(),
      ];
    }
  }, []);

  // Filter out users with AccountType 2 (Parent), 3 (Guidance), or 4 (Other)
  // Only include AccountType 1 (Student) or undefined (defaults to student)
  const filteredStudents = useMemo(() => {
    return students.filter((student) => {
      const accountType = student.accountType;
      // Convert to number if it's a string (handle both string and number formats)
      const accountTypeNum = accountType !== undefined && accountType !== null
        ? (typeof accountType === 'string' ? parseInt(accountType, 10) : accountType)
        : undefined;
      // Include if accountType is undefined, null, or 1 (Student)
      // Exclude if accountType is 2 (Parent), 3 (Guidance), or 4 (Other)
      return accountTypeNum === undefined || accountTypeNum === null || accountTypeNum === 1;
    });
  }, [students]);

  // Count students by graduating class
  const studentsByClass = useMemo(() => {
    const counts: Record<string, number> = {};
    graduatingClasses.forEach((classYear) => {
      counts[classYear] = 0;
    });

    filteredStudents.forEach((student) => {
      const studentClassYear = student.grade; // grade is the Class Year
      if (graduatingClasses.includes(studentClassYear)) {
        counts[studentClassYear] = (counts[studentClassYear] || 0) + 1;
      }
    });

    return counts;
  }, [filteredStudents, graduatingClasses]);

  // Calculate total students across all classes
  const totalStudents = useMemo(() => {
    return Object.values(studentsByClass).reduce((sum, count) => sum + count, 0);
  }, [studentsByClass]);

  // Prepare chart data
  const chartData = useMemo(() => {
    return {
      labels: graduatingClasses,
      datasets: [
        {
          label: 'Active Student Profiles',
          data: graduatingClasses.map((classYear) => studentsByClass[classYear] || 0),
          backgroundColor: 'rgba(25, 118, 210, 0.8)',
          borderColor: 'rgba(25, 118, 210, 1)',
          borderWidth: 1,
        },
      ],
    };
  }, [graduatingClasses, studentsByClass]);

  // Count favorite schools (only for current students in next 4 graduating classes)
  const favoriteSchoolsData = useMemo(() => {
    const schoolCounts: Record<string, number> = {};
    const studentsWithFavorites = new Set<string>();

    // Filter to only include current students (in the next 4 graduating classes)
    const currentStudents = filteredStudents.filter((student) => {
      const studentClassYear = student.grade; // grade is the Class Year
      return graduatingClasses.includes(studentClassYear);
    });

    currentStudents.forEach((student) => {
      if (student.favoriteSchools && student.favoriteSchools.length > 0) {
        studentsWithFavorites.add(student.uid);
        student.favoriteSchools.forEach((schoolName) => {
          schoolCounts[schoolName] = (schoolCounts[schoolName] || 0) + 1;
        });
      }
    });

    // Convert to array and sort by count descending
    const sortedSchools = Object.entries(schoolCounts)
      .map(([schoolName, count]) => {
        // Find school data by name
        const school = schools.find((s) => s.name === schoolName);
        // Use same logo priority as Dashboard: feedIcon first, then resultsImage
        const schoolIcon = school?.feedIcon || school?.resultsImage;
        return {
          name: schoolName,
          count,
          logo: schoolIcon || undefined,
          id: school?.id || undefined,
        };
      })
      .sort((a, b) => b.count - a.count);

    return {
      schools: sortedSchools,
      studentsWithFavorites: studentsWithFavorites.size,
      uniqueSchools: sortedSchools.length,
    };
  }, [filteredStudents, schools, graduatingClasses]);

  // Count favorite programs (only for current students in next 4 graduating classes)
  const favoriteProgramsData = useMemo(() => {
    const programCounts: Record<string, { count: number; programName: string; schoolName: string; logo: string | undefined; programId: string | undefined }> = {};
    const studentsWithFavorites = new Set<string>();

    // Filter to only include current students (in the next 4 graduating classes)
    const currentStudents = filteredStudents.filter((student) => {
      const studentClassYear = student.grade; // grade is the Class Year
      return graduatingClasses.includes(studentClassYear);
    });

    currentStudents.forEach((student) => {
      // Use favoriteProgramsWithSchools if available, otherwise fall back to favoritePrograms with lookup
      if (student.favoriteProgramsWithSchools && student.favoriteProgramsWithSchools.length > 0) {
        studentsWithFavorites.add(student.uid);
        student.favoriteProgramsWithSchools.forEach((formattedProgram) => {
          // Format: "Program Name - School Name"
          const parts = formattedProgram.split(' - ');
          const programName = parts[0];
          const schoolName = parts.length > 1 ? parts[1] : '';
          
          // Skip if school name is empty
          if (!schoolName) {
            return;
          }
          
          // Find school data by name to get logo
          const school = schools.find((s) => s.name === schoolName);
          
          // Skip if school is not found in schools list (deleted school)
          if (!school) {
            return;
          }
          
          // Use the formatted string as the key for consistency
          const key = formattedProgram;
          
          if (!programCounts[key]) {
            // Find school data by name to get logo
            const school = schools.find((s) => s.name === schoolName);
            const schoolIcon = school?.feedIcon || school?.resultsImage;
            
            // Try to find program ID from ProgramsContext
            let programId: string | undefined;
            if (programName && schoolName) {
              const program = programs.find((p) => p.program === programName && p.schoolName === schoolName);
              programId = program?.id;
            }
            
            programCounts[key] = {
              count: 0,
              programName: programName,
              schoolName: schoolName,
              logo: schoolIcon || undefined,
              programId: programId,
            };
          }
          programCounts[key].count++;
        });
      } else if (student.favoritePrograms && student.favoritePrograms.length > 0) {
        // Fallback: use old method if favoriteProgramsWithSchools is not available
        studentsWithFavorites.add(student.uid);
        student.favoritePrograms.forEach((programName) => {
          // Find program by name
          const program = programs.find((p) => p.program === programName);
          if (program && program.schoolName) {
            // Find school data by name to get logo
            const school = schools.find((s) => s.name === program.schoolName);
            
            // Skip if school is not found in schools list (deleted school)
            if (!school) {
              return;
            }
            
            const key = `${program.program}|${program.schoolName}`; // Use program name + school name as key
            if (!programCounts[key]) {
              const schoolIcon = school?.feedIcon || school?.resultsImage;
              programCounts[key] = {
                count: 0,
                programName: program.program,
                schoolName: program.schoolName,
                logo: schoolIcon || undefined,
                programId: program.id || undefined,
              };
            }
            programCounts[key].count++;
          }
        });
      }
    });

    // Convert to array, filter out programs with missing schools, and sort by count descending
    const sortedPrograms = Object.values(programCounts)
      .filter((program) => {
        // Filter out programs where school is not found in schools list
        if (!program.schoolName) {
          return false;
        }
        const school = schools.find((s) => s.name === program.schoolName);
        return !!school; // Only include if school exists
      })
      .sort((a, b) => b.count - a.count);

    return {
      programs: sortedPrograms,
      studentsWithFavorites: studentsWithFavorites.size,
      uniquePrograms: sortedPrograms.length,
    };
  }, [filteredStudents, programs, schools, graduatingClasses]);

  // Format event date and time for display (matching Dashboard format)
  const formatEventDateTime = (dateStr: string, timeStr: string): string => {
    if (!dateStr) return 'N/A';
    
    try {
      // Parse the date string - handle formats like "M/D/YYYY" or "MM/DD/YYYY"
      const date = parseDateSafariCompatible(dateStr);
      if (isNaN(date.getTime())) return 'N/A';
      
      // Format date as "MMM DD, YYYY" (e.g., "Nov 1, 2025")
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = monthNames[date.getMonth()];
      const day = date.getDate();
      const year = date.getFullYear();
      
      const formattedDate = `${month} ${day}, ${year}`;
      
      // Format time from "HH:MM AM/PM" to "H:MMa/p" (e.g., "9:30a" or "2:30p")
      let formattedTime = '';
      if (timeStr) {
        const timeMatch = timeStr.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
        if (timeMatch) {
          let hours = parseInt(timeMatch[1], 10);
          const minutes = timeMatch[2];
          const period = timeMatch[3].toUpperCase();
          
          // Keep hours in 12-hour format (don't convert to 24-hour)
          // Format as "H:MMa" or "H:MMp" (no leading zero on hours)
          const ampm = period.charAt(0).toLowerCase();
          formattedTime = `${hours}:${minutes}${ampm}`;
        } else {
          // If format doesn't match, try to parse and format it
          formattedTime = timeStr;
        }
      }
      
      return formattedTime ? `${formattedDate} (${formattedTime})` : formattedDate;
    } catch (error) {
      console.error('Error formatting event date:', error);
      return 'N/A';
    }
  };

  // Get all favorited schools from current students
  const favoritedSchoolNames = useMemo(() => {
    const schoolSet = new Set<string>();
    const currentStudents = filteredStudents.filter((student) => {
      const studentClassYear = student.grade;
      return graduatingClasses.includes(studentClassYear);
    });
    
    currentStudents.forEach((student) => {
      if (student.favoriteSchools && student.favoriteSchools.length > 0) {
        student.favoriteSchools.forEach((schoolName) => {
          schoolSet.add(schoolName);
        });
      }
    });
    
    // Also include the guidance user's high school name
    if (user?.schoolName) {
      schoolSet.add(user.schoolName);
    }
    
    return Array.from(schoolSet);
  }, [filteredStudents, graduatingClasses, user?.schoolName]);

  // Filter and process upcoming events from favorited schools
  const upcomingEvents = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Process events similar to Dashboard - convert startTime to date/time format
    const processedEvents = allEvents.map(event => {
      let dateStr = '';
      let timeStr = '';
      
      if (event.startTime) {
        let startTimeStr = event.startTime;
        if (typeof startTimeStr === 'string') {
          startTimeStr = startTimeStr.replace(' ', 'T');
        }
        const dateObj = new Date(startTimeStr);
        if (!isNaN(dateObj.getTime())) {
          // Use toLocaleDateString and toLocaleTimeString matching Dashboard format
          dateStr = dateObj.toLocaleDateString();
          timeStr = dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        } else {
          dateStr = '';
          timeStr = '';
        }
      }
      
      return {
        ...event,
        processedDate: dateStr,
        processedTime: timeStr,
        originalStartTime: event.startTime
      };
    });

    // Filter for upcoming events from favorited schools
    const filtered = processedEvents.filter((event) => {
      // Check if event is from a favorited school
      const eventHost = event.host || event.hostName || '';
      const isFromFavoritedSchool = favoritedSchoolNames.includes(eventHost);
      
      if (!isFromFavoritedSchool) return false;

      // Check if event is upcoming
      if (!event.originalStartTime) return false;
      
      const timeStrFormatted = typeof event.originalStartTime === 'string' 
        ? event.originalStartTime.replace(' ', 'T') 
        : event.originalStartTime;
      const dt = parseDateSafariCompatible(timeStrFormatted);
      
      if (isNaN(dt.getTime())) return false;
      
      dt.setHours(0, 0, 0, 0);
      return dt >= today;
    });

    // Sort by soonest date+time
    filtered.sort((a, b) => {
      const aTimeStr = a.originalStartTime;
      const bTimeStr = b.originalStartTime;
      
      if (!aTimeStr || !bTimeStr) {
        // Handle undefined/null - put undefined at the end
        if (!aTimeStr && !bTimeStr) return 0;
        if (!aTimeStr) return 1;
        if (!bTimeStr) return -1;
      }
      
      const aTimeStrFormatted = typeof aTimeStr === 'string' ? aTimeStr.replace(' ', 'T') : '';
      const bTimeStrFormatted = typeof bTimeStr === 'string' ? bTimeStr.replace(' ', 'T') : '';
      
      if (!aTimeStrFormatted || !bTimeStrFormatted) {
        if (!aTimeStrFormatted && !bTimeStrFormatted) return 0;
        if (!aTimeStrFormatted) return 1;
        if (!bTimeStrFormatted) return -1;
      }
      
      return parseDateSafariCompatible(aTimeStrFormatted).getTime() - parseDateSafariCompatible(bTimeStrFormatted).getTime();
    });

    return filtered;
  }, [allEvents, favoritedSchoolNames]);

  // Paginate events - 5 events per page
  const totalPages = Math.ceil(upcomingEvents.length / eventsPerPage);
  const displayedEvents = useMemo(() => {
    const startIndex = (eventsPage - 1) * eventsPerPage;
    return upcomingEvents.slice(startIndex, startIndex + eventsPerPage);
  }, [upcomingEvents, eventsPage, eventsPerPage]);

  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    setEventsPage(page);
  };

  // Reset to page 1 when events change
  useEffect(() => {
    setEventsPage(1);
  }, [upcomingEvents.length]);

  // Get school logo for event host
  const getSchoolLogo = (hostName: string): string | undefined => {
    if (!hostName) return undefined;
    
    // Check if event is from guidance user's high school
    if (hostName === user?.schoolName && user?.userIcon) {
      return user.userIcon;
    }
    
    // Check if it's Fosterway
    if (hostName.toLowerCase().includes('fosterway')) {
      return "https://firebasestorage.googleapis.com/v0/b/fosterwayapp.appspot.com/o/schoolIconSquare%2FFosterway%20University?alt=media&token=f129a2e4-cac6-4457-99fb-b917b4af94cd";
    }
    
    // Find school in schools context
    const school = schools.find((s) => s.name === hostName);
    return school?.feedIcon || school?.resultsImage || undefined;
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
        },
      },
    },
  };


  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={3}>
        {/* Favourite Schools List */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Favourite Schools
            </Typography>
            {/* School List with Logos - Single Column Layout */}
            <Box sx={{ maxHeight: '450px', overflowY: 'auto', mb: 2 }}>
              {favoriteSchoolsData.schools.map((school) => (
                <Box 
                  key={school.name} 
                  onClick={() => {
                    // Navigate to Students page with the school name filter applied
                    navigate(`/students?searchFavoriteSchools=${encodeURIComponent(school.name)}&fromGuidance=1`);
                  }}
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1, 
                    py: 0.5,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <Avatar
                    sx={{ 
                      width: 32, 
                      height: 32,
                      bgcolor: 'grey.200',
                      color: 'grey.600',
                      fontWeight: 600,
                      flexShrink: 0,
                    }}
                  >
                    {school.logo ? (
                      <img
                        src={school.logo}
                        alt={school.name}
                        style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '50%', border: '0.5px solid black' }}
                      />
                    ) : (
                      school.name ? school.name[0] : ''
                    )}
                  </Avatar>
                  <Box sx={{ flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', minWidth: 0 }}>
                    <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.95rem', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {school.name}
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.95rem', ml: 1, flexShrink: 0 }}>
                      {school.count}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              {favoriteSchoolsData.studentsWithFavorites} students have selected {favoriteSchoolsData.uniqueSchools} schools as favourites.
            </Typography>
          </Paper>
        </Grid>

        {/* Favourite Programs List */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Favourite Programs
            </Typography>
            {/* Program List with Logos - Single Column Layout */}
            <Box sx={{ maxHeight: '450px', overflowY: 'auto', mb: 2 }}>
              {favoriteProgramsData.programs.map((program, index) => (
                <Box 
                  key={`${program.programName}-${program.schoolName}-${index}`} 
                  onClick={() => {
                    if (program.programId) {
                      navigate(`/programs/${program.programId}?fromGuidance=1`);
                    } else {
                      console.error('Program ID not found for:', program.programName);
                    }
                  }}
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1, 
                    py: 0.5,
                    cursor: program.programId ? 'pointer' : 'default',
                    '&:hover': program.programId ? {
                      backgroundColor: 'action.hover',
                    } : {},
                  }}
                >
                  <Avatar
                    sx={{ 
                      width: 32, 
                      height: 32,
                      bgcolor: 'grey.200',
                      color: 'grey.600',
                      fontWeight: 600,
                      flexShrink: 0,
                    }}
                  >
                    {program.logo ? (
                      <img
                        src={program.logo}
                        alt={program.schoolName}
                        style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '50%', border: '0.5px solid black' }}
                      />
                    ) : (
                      program.schoolName ? program.schoolName[0] : ''
                    )}
                  </Avatar>
                  <Box sx={{ flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', minWidth: 0 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.95rem', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {program.programName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {program.schoolName}
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.95rem', ml: 1, flexShrink: 0 }}>
                      {program.count}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              {favoriteProgramsData.studentsWithFavorites} students have selected {favoriteProgramsData.uniquePrograms} programs as favourites.
            </Typography>
          </Paper>
        </Grid>

        {/* Active Student Profiles by Class Chart */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Active Student Profiles by Class
            </Typography>
            <Box sx={{ height: '450px', mb: 2 }}>
              <Bar data={chartData} options={chartOptions} />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              {totalStudents} current students have active profiles.
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Upcoming Events at Favourited Schools */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Upcoming Events at Favourited Schools
        </Typography>
        {eventsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
            <CircularProgress />
          </Box>
        ) : upcomingEvents.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            No upcoming events from favourited schools.
          </Typography>
        ) : (
          <>
            <Box sx={{ width: '100%', overflowX: 'auto' }}>
              <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', minWidth: isMobile ? '100%' : 800 }}>
                <Box component="thead">
                  <Box component="tr" sx={{ fontSize: '0.85rem', fontWeight: 'bold', fontFamily: 'Roboto, Arial, sans-serif' }}>
                    <Box component="th" sx={{ 
                      p: 1, 
                      textAlign: 'left', 
                      verticalAlign: 'bottom', 
                      fontSize: '0.85rem', 
                      fontWeight: 'bold',
                      width: isMobile ? '35%' : 'auto'
                    }}>
                      Date
                    </Box>
                    <Box component="th" sx={{ p: 1, textAlign: 'left', verticalAlign: 'bottom', fontSize: '0.85rem', fontWeight: 'bold' }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
                        <Box sx={{ width: 32, height: 32, mr: 2 }}></Box>
                        Event Host
                      </Box>
                    </Box>
                    <Box component="th" sx={{ p: 1, textAlign: 'left', verticalAlign: 'bottom', fontSize: '0.85rem', fontWeight: 'bold' }}>
                      Event Name
                    </Box>
                    <Box component="th" sx={{ p: 1, textAlign: 'left', verticalAlign: 'bottom', fontSize: '0.85rem', fontWeight: 'bold' }}>
                      Location
                    </Box>
                  </Box>
                </Box>
                <Box component="tbody">
                  {displayedEvents.map((event) => {
                    const hostName = event.host || event.hostName || '';
                    const schoolLogo = getSchoolLogo(hostName);
                    return (
                      <Box
                        component="tr"
                        key={event.id}
                        sx={{ 
                          cursor: 'pointer', 
                          '&:hover': { backgroundColor: 'grey.50' }, 
                          borderBottom: 1, 
                          borderColor: 'divider',
                          fontSize: '0.85rem',
                          fontFamily: 'Roboto, Arial, sans-serif'
                        }}
                        onClick={() => navigate(`/events/${event.id}`, { state: { event } })}
                      >
                        <Box component="td" sx={{ 
                          p: 1, 
                          textAlign: 'left', 
                          fontSize: '0.85rem', 
                          fontWeight: 400,
                          width: isMobile ? '35%' : 'auto',
                          maxWidth: isMobile ? '35%' : 'none',
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word',
                          whiteSpace: isMobile ? 'normal' : 'nowrap'
                        }}>
                          {formatEventDateTime(event.processedDate || '', event.processedTime || '')}
                        </Box>
                        <Box component="td" sx={{ p: 1, textAlign: 'left', fontSize: '0.85rem', fontWeight: 400, display: 'flex', alignItems: 'center' }}>
                          {schoolLogo && (
                            <Avatar sx={{ width: 32, height: 32, bgcolor: 'grey.200', color: 'grey.600', fontWeight: 600, mr: 2 }}>
                              <img 
                                src={schoolLogo} 
                                alt={hostName} 
                                style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '50%', border: '0.5px solid black' }} 
                              />
                            </Avatar>
                          )}
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {hostName}
                            {event.private && (
                              <LockOutlinedIcon fontSize="small" sx={{ ml: 1, verticalAlign: 'middle' }} />
                            )}
                          </Box>
                        </Box>
                        <Box component="td" sx={{ p: 1, textAlign: 'left', fontSize: '0.85rem', fontWeight: 400 }}>
                          {event.name || event.title || 'N/A'}
                        </Box>
                        <Box component="td" sx={{ p: 1, textAlign: 'left', fontSize: '0.85rem', fontWeight: 400 }}>
                          {event.location || event.address || 'N/A'}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Box>
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Pagination
                  count={totalPages}
                  page={eventsPage}
                  onChange={handlePageChange}
                  color="primary"
                  size="medium"
                />
              </Box>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
};

export default GuidanceDashboard;
